// combatpro/views/DiceView.js
//
// Combat PRO - DiceView (parity restoration of the full working Dice Roller).
//
// The modular migration shipped this page as a stub: the animated dice, the
// Ability Checks card and the Saving Throws card were deferred and never ported,
// so a roll only produced a "Total: N" line and the big result area stayed empty
// (it looked like nothing happened). This restores the established three-card
// Dice Roller shown in the working product:
//   - Quick Roll (DiceControls) rolls the accumulated selection.
//   - Ability Checks rolls d20 + the active monster's ability modifier.
//   - Saving Throws rolls d20 + the active monster's stored save bonus.
//   - Every roll drives the animated polyhedra dice (DiceFaces) and appends to
//     the Roll History, newest-first, capped at 10.
//
// All rolls are self-contained here using the pure rollDie engine and real
// monster state (state.monsters[state.currentCombatantIndex]); no context
// changes, so nothing else in the app is affected. The result carries a per-die
// { sides, value } list so DiceFaces can draw the correct polyhedron for each
// die, plus rolls/total/formula/label for history and the total line.
//
// No getElementById, no innerHTML, no optional chaining, no em dash.

import React, { useContext, useState } from 'react';
import { CombatContext } from '../contexts/CombatContext';
import DiceControls from '../dice/DiceControls';
import DiceFaces from '../dice/DiceFaces';
import DiceResult from '../dice/DiceResult';
import DiceHistory from '../dice/DiceHistory';
import { rollDie } from '../dice/diceEngine';

var HISTORY_LIMIT = 10;
var DICE_SIDES = [4, 6, 8, 10, 12, 20, 100];
var ABILITIES = ['str', 'dex', 'con', 'int', 'wis', 'cha'];

function DiceView() {
  const { state, setState } = useContext(CombatContext);
  const [selection, setSelection] = useState({});
  const [rollId, setRollId] = useState(0);

  function commitResult(result) {
    setState(function (previous) {
      var nextHistory = [result].concat(previous.diceHistory);
      if (nextHistory.length > HISTORY_LIMIT) { nextHistory = nextHistory.slice(0, HISTORY_LIMIT); }
      return Object.assign({}, previous, { currentDiceResult: result, diceHistory: nextHistory });
    });
    setRollId(function (n) { return n + 1; });
  }

  function handleSelectDie(sides) {
    setSelection(function (previous) {
      var next = {};
      for (var key in previous) {
        if (Object.prototype.hasOwnProperty.call(previous, key)) { next[key] = previous[key]; }
      }
      next[sides] = (next[sides] || 0) + 1;
      return next;
    });
  }

  function handleClearSelection() { setSelection({}); }

  function handleRollSelected() {
    var dice = [];
    var formulaParts = [];
    for (var i = 0; i < DICE_SIDES.length; i++) {
      var sides = DICE_SIDES[i];
      var count = selection[sides] || 0;
      if (count > 0) {
        for (var r = 0; r < count; r++) { dice.push({ sides: sides, value: rollDie(sides) }); }
        formulaParts.push(count + 'd' + sides);
      }
    }
    if (dice.length === 0) { return; }
    var total = dice.reduce(function (a, d) { return a + d.value; }, 0);
    commitResult({ dice: dice, rolls: dice.map(function (d) { return d.value; }), total: total, formula: formulaParts.join('+'), label: 'Custom Roll' });
    setSelection({});
  }

  function activeMonster() { return state.monsters[state.currentCombatantIndex]; }

  function handleAbilityCheck(ability) {
    var monster = activeMonster();
    if (!monster) { return; }
    var scores = monster.abilityScores || {};
    var score = (scores[ability] === undefined || scores[ability] === null) ? 10 : scores[ability];
    var mod = Math.floor((score - 10) / 2);
    var raw = rollDie(20);
    var modStr = (mod >= 0 ? '+' : '') + mod;
    commitResult({ dice: [{ sides: 20, value: raw }], rolls: [raw], total: raw + mod, formula: 'd20' + modStr, label: ability.toUpperCase() + ' Check' });
  }

  function handleSave(ability) {
    var monster = activeMonster();
    if (!monster) { return; }
    var saves = monster.saves || {};
    var bonus = (saves[ability] === undefined || saves[ability] === null) ? 0 : saves[ability];
    var raw = rollDie(20);
    var bonusStr = (bonus >= 0 ? '+' : '') + bonus;
    commitResult({ dice: [{ sides: 20, value: raw }], rolls: [raw], total: raw + bonus, formula: 'd20' + bonusStr, label: ability.toUpperCase() + ' Save' });
  }

  function handleClearHistory() {
    setState(function (previous) {
      return Object.assign({}, previous, { diceHistory: [], currentDiceResult: null });
    });
    setRollId(function (n) { return n + 1; });
  }

  var currentDice = state.currentDiceResult ? state.currentDiceResult.dice : null;

  return (
    <div className="vi-view" id="view-dice">
      <div id="vi-dice-display" style={{ marginBottom: '16px', background: '#1a1a2e', border: '2px solid #333', borderRadius: '12px', overflow: 'hidden', position: 'relative' }}>
        <div style={{ fontSize: '15px', fontWeight: 600, color: '#ffffff', textTransform: 'uppercase', letterSpacing: '1.5px', padding: '10px 16px 4px' }}>Roll Result</div>
        <div id="vi-dice-faces" style={{ position: 'relative', width: '100%', height: '160px', overflow: 'hidden' }}>
          <DiceFaces dice={currentDice} rollId={rollId} />
        </div>
        <div id="vi-dice-total" style={{ display: state.currentDiceResult === null ? 'none' : 'block', padding: '8px 16px 12px', fontSize: '24px', fontWeight: 700, color: '#fff', borderTop: '1px solid #2a2a4a' }}>
          <DiceResult currentDiceResult={state.currentDiceResult} />
        </div>
      </div>

      <div className="vi-dice-cards-grid">
        <DiceControls selection={selection} onSelectDie={handleSelectDie} onClearSelection={handleClearSelection} onRollSelected={handleRollSelected} />

        <div className="vi-dice-card">
          <h4 className="vi-dice-card-title vi-dice-title-ability">Ability Checks</h4>
          <div className="vi-dice-abil-grid">
            {ABILITIES.map(function (ab) {
              return (
                <button key={ab} type="button" className="vi-dice-btn vi-dice-btn-ability" onClick={function () { handleAbilityCheck(ab); }}>{ab.toUpperCase()}</button>
              );
            })}
          </div>
        </div>

        <div className="vi-dice-card">
          <h4 className="vi-dice-card-title vi-dice-title-save">Saving Throws</h4>
          <div className="vi-dice-abil-grid">
            {ABILITIES.map(function (ab) {
              return (
                <button key={ab} type="button" className="vi-dice-btn vi-dice-btn-save" onClick={function () { handleSave(ab); }}>{ab.toUpperCase()} Save</button>
              );
            })}
          </div>
        </div>
      </div>

      <div id="vi-dice-mount">
        <DiceHistory diceHistory={state.diceHistory} onClearHistory={handleClearHistory} />
      </div>
    </div>
  );
}

export default DiceView;
